import { createSelector } from 'reselect';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';

export const getMultipleTradeInsState = createSelector(
    [(state: OfferReduxBaseState) => state.tradeIns],
    (tradeIns) => tradeIns || {}
);

export const getTradeInsScopedState = createSelector(
    [(state: OfferReduxBaseState) => state.tradeInsScoped],
    (tradeInsScoped) => tradeInsScoped || {}
);

export const getAllTradeIns = createSelector([getMultipleTradeInsState, getTradeInsScopedState], (multiState, scopedState) => {
    const items = multiState.items || [];
    return items.map((item: any, index: any) => ({
        ...item,
        tradeIn: {
            ...item.tradeIn,
            shouldIncludeTradeIn: scopedState.shouldIncludeTradeIns?.[index] ?? false
        }
    }));
});

export const getTradeInByIndex = createSelector(
    [getAllTradeIns, (_, tradeInIndex: number) => tradeInIndex],
    (allTradeIns, tradeInIndex) => {
        return allTradeIns[tradeInIndex]?.tradeIn || {};
    }
);

export const getTradeYear = (state: OfferReduxBaseState, tradeInIndex: number) => {
    const year = getTradeInByIndex(state, tradeInIndex).year;
    return year ? year.toString() : '';
};

export const getTradeMake = (state: OfferReduxBaseState, tradeInIndex: number) => {
    return getTradeInByIndex(state, tradeInIndex).make || '';
};

export const getTradeModel = (state: OfferReduxBaseState, tradeInIndex: number) => {
    return getTradeInByIndex(state, tradeInIndex).model || '';
};

export const getTradeTrim = (state: OfferReduxBaseState, tradeInIndex: number) => {
    return getTradeInByIndex(state, tradeInIndex).trim || '';
};

export const getTradeValue = (state: OfferReduxBaseState, tradeInIndex: number) => {
    return getTradeInByIndex(state, tradeInIndex).value || 0;
};

export const getTradeInAmountOwed = (state: OfferReduxBaseState, tradeInIndex: number) => {
    return getTradeInByIndex(state, tradeInIndex).amountOwed || 0;
};

export const getTradeSource = (state: OfferReduxBaseState, tradeInIndex: number) => {
    return getTradeInByIndex(state, tradeInIndex).source || '';
};

export const getIncludedTradeIns = (state: OfferReduxBaseState) => {
    const allTradeIns = getAllTradeIns(state);
    return allTradeIns.filter((tradeInItem: any) => !tradeInItem.isDeleted && tradeInItem.tradeIn.shouldIncludeTradeIn);
};

export const getIncludedTradeInsWithOriginalIndexes = (state: OfferReduxBaseState) => {
    const allTradeIns = getAllTradeIns(state);
    return allTradeIns
        .map((tradeInItem: any, index: number) => ({ ...tradeInItem, originalIndex: index }))
        .filter((tradeInItem: any) => !tradeInItem.isDeleted && tradeInItem.tradeIn.shouldIncludeTradeIn);
};

export const getTradeInSummary = createSelector([getIncludedTradeIns], (includedTradeIns) => {
    const totalAllowance = includedTradeIns.reduce((sum: number, item: any) => sum + (item.tradeIn.value || 0), 0);
    const totalAcv = includedTradeIns.reduce((sum: number, item: any) => sum + (item.tradeIn.acv || 0), 0);
    const totalPayoff = includedTradeIns.reduce((sum: number, item: any) => sum + (item.tradeIn.owed || 0), 0);
    const totalTradeInTaxReduction = includedTradeIns.reduce(
        (sum: number, item: any) => sum + (item.tradeIn.tradeInTaxReduction || 0),
        0
    );
    const netTrade = totalAllowance - totalPayoff;

    return {
        netTrade,
        totalAcv,
        totalAllowance,
        totalPayoff,
        totalTradeInTaxReduction
    };
});

export const getIncludedTradeInsTotalValue = (state: OfferReduxBaseState): number => {
    return getTradeInSummary(state).totalAllowance;
};

export const getTotalNetTradeValueForIncludedActiveTradeIns = (state: OfferReduxBaseState): number => {
    return getTradeInSummary(state).netTrade;
};
